/**
 * Docs landing (`/docs`) — in-app developer docs hub.
 *
 * Same shape as Home's "fresh" Quickstart but persistent, with
 * deeper explanation and all-language snippets. Three columns on
 * desktop: TOC, content, Try-it. `05-pages.md` §5c.
 */
import { CodeSnippet } from '@/components/primitives/CodeSnippet';
import { CopyPrompt } from '@/components/primitives/CopyPrompt';
import { RunItPanel } from '@/components/primitives/RunItPanel';
import { Island } from '@/components/shell/Island';
import { Button } from '@/components/ui/button';
import { AGENT_SETUP_PROMPT } from '@/lib/agent-onboarding';
import {
  ArrowRight,
  BookOpen,
  Broadcast,
  Code,
  Lightning,
  Package,
  Robot,
  WebhooksLogo,
} from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_app/docs/')({
  component: DocsLanding,
});

const CHAPTERS = [
  {
    href: '/docs/agent',
    icon: Robot,
    label: 'AI agent setup',
    blurb:
      'Let Claude Code, Cursor, or Claude Desktop install the CLI, sign you in, and run setup — then drive it in natural language.',
  },
  {
    href: '/docs/pixel',
    icon: Broadcast,
    label: 'Install the pixel',
    blurb: 'One script tag for real-user analytics — traffic, sources, devices, Core Web Vitals.',
  },
  {
    href: '/docs/quickstart',
    icon: Lightning,
    label: 'API quickstart',
    blurb: 'Mint a key, POST an audit, poll until complete. End-to-end in 3 minutes.',
  },
  {
    href: '/docs/api',
    icon: Code,
    label: 'API reference',
    blurb: 'Every public endpoint, with request shape, response, and error codes.',
  },
  {
    href: '/docs/webhooks',
    icon: WebhooksLogo,
    label: 'Webhooks',
    blurb: 'Payload shape, signature verification, replay semantics.',
  },
  {
    href: '/docs/changelog',
    icon: BookOpen,
    label: 'Changelog',
    blurb: 'What shipped, in reverse chronological order.',
  },
] as const;

function DocsLanding(): ReactElement {
  return (
    <Island breadcrumb={[{ label: 'Docs' }]}>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-6">
          <header className="space-y-2">
            <h1 className="text-2xl font-semibold text-[var(--color-ink)]">Set up Clickweave</h1>
            <p className="text-sm text-[var(--color-mute)]">
              Three ways in: let your AI agent set it up, drop in the no-code analytics pixel, or
              call the API directly. Pick your path below.
            </p>
          </header>

          <section className="space-y-3 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
            <div className="flex items-center gap-2">
              <Robot
                aria-hidden="true"
                size={22}
                weight="light"
                className="text-[var(--color-mute)]/80"
              />
              <h2 className="text-base font-semibold text-[var(--color-ink)]">
                The fastest way in: your AI agent
              </h2>
            </div>
            <p className="text-sm text-[var(--color-mute)]">
              Paste this into Claude Code, Cursor, or any AI coding agent. It installs the
              Clickweave skill and CLI, signs you in, and runs your first audit — you drive the rest
              in plain language.
            </p>
            <CopyPrompt prompt={AGENT_SETUP_PROMPT} />
            <Button size="sm" variant="ghost" render={<Link to="/docs/agent" />}>
              How agent setup works
              <ArrowRight aria-hidden="true" />
            </Button>
          </section>

          <section className="grid gap-3 sm:grid-cols-2">
            {CHAPTERS.map((c) => (
              <Link
                key={c.href}
                to={c.href}
                className="group flex items-start gap-3 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4 transition-colors hover:border-[var(--color-line-strong)] hover:bg-[var(--color-paper-alt)]/40"
              >
                <c.icon
                  aria-hidden="true"
                  size={24}
                  weight="light"
                  className="text-[var(--color-mute)]/80"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-1 text-sm font-semibold text-[var(--color-ink)]">
                    {c.label}
                    <ArrowRight
                      aria-hidden="true"
                      size={14}
                      className="transition-transform group-hover:translate-x-0.5"
                    />
                  </div>
                  <p className="mt-0.5 text-xs text-[var(--color-mute)]">{c.blurb}</p>
                </div>
              </Link>
            ))}
          </section>

          <section className="space-y-3 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
            <h2 className="text-base font-semibold text-[var(--color-ink)]">Prefer raw HTTP?</h2>
            <p className="text-sm text-[var(--color-mute)]">
              Every capability is a plain REST call too. This one POSTs a one-off audit — run it
              in-app from the panel on the right, or copy it into your terminal.
            </p>
            <CodeSnippet
              code={
                'curl https://api.clickweave.co/v1/audits \\\n' +
                "  -H 'Authorization: Bearer $API_KEY' \\\n" +
                "  -H 'Content-Type: application/json' \\\n" +
                '  -d \'{"url":"https://example.com"}\''
              }
              variables={{ API_KEY: '<YOUR_API_KEY>' }}
            />
          </section>
        </div>

        <aside className="space-y-4">
          <section className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
            <div className="mb-2 text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
              Try it
            </div>
            <RunItPanel languages={['curl']} />
            <div className="mt-3 text-xs text-[var(--color-mute)]">
              Runs against your workspace using your Clerk session. Create a key in{' '}
              <Link to="/settings/keys" className="underline">
                API keys
              </Link>{' '}
              to run outside the dashboard.
            </div>
          </section>
          <section className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
            <div className="mb-2 text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
              Need help?
            </div>
            <p className="text-sm text-[var(--color-mute)]">
              The{' '}
              <Link to="/help" className="underline">
                Help page
              </Link>{' '}
              has a direct email and keyboard shortcut reference.
            </p>
          </section>
        </aside>
      </div>
    </Island>
  );
}
